import types from '../mutation-types';
import BookingSettingsAPI from '../../api/bookingSettings';

/**
 * OCHAT: estado de la configuración del hotel.
 *
 * POR QUÉ UN SOLO ARREGLO DE PROPIEDADES
 * La API devuelve cada propiedad con sus tipos de habitación y, dentro de cada uno,
 * sus planes de tarifa. Guardar tres colecciones planas y volver a armar el árbol en
 * la pantalla sería trabajo de más y una fuente de estados incoherentes.
 *
 * POR QUÉ SE VUELVE A PEDIR LA PROPIEDAD DESPUÉS DE CADA ESCRITURA
 * Empalmar a mano un plan de tarifa nuevo dentro del tipo de habitación correcto,
 * dentro de la propiedad correcta, es exactamente donde se cuelan los errores de
 * estado. Y hay un caso que lo vuelve obligatorio: borrar un plan de tarifa con el
 * que ya se vendió NO lo borra, lo desactiva, y el servidor responde distinto según
 * el caso. Volver a pedir la propiedad es correcto en las dos ramas. Es una pantalla
 * de configuración que se usa de a ratos, no una bandeja: el viaje extra no se nota.
 */
export const state = {
  properties: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isApplying: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getProperties(_state) {
    return _state.properties;
  },
  getProperty: _state => id =>
    _state.properties.find(property => property.id === Number(id)),
  /** Aplanado para los selectores de la carga por rango. */
  getRoomTypes: _state => propertyId => {
    const property = _state.properties.find(
      item => item.id === Number(propertyId)
    );
    return property?.room_types ?? [];
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isFetching: true });
    try {
      const response = await BookingSettingsAPI.properties();
      commit(types.SET_BOOKING_PROPERTIES, response.data.payload);
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, id) => {
    const response = await BookingSettingsAPI.property(id);
    commit(types.SET_BOOKING_PROPERTY, response.data);
    return response.data;
  },

  // ------------------------------------------------------------- propiedades

  createProperty: async ({ commit }, payload) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isCreating: true });
    try {
      const response = await BookingSettingsAPI.createProperty(payload);
      commit(types.SET_BOOKING_PROPERTY, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isCreating: false });
    }
  },

  updateProperty: async ({ commit }, { id, ...payload }) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isUpdating: true });
    try {
      const response = await BookingSettingsAPI.updateProperty(id, payload);
      commit(types.SET_BOOKING_PROPERTY, response.data);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isUpdating: false });
    }
  },

  deleteProperty: async ({ commit }, id) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isDeleting: true });
    try {
      await BookingSettingsAPI.deleteProperty(id);
      commit(types.REMOVE_BOOKING_PROPERTY, id);
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isDeleting: false });
    }
  },

  // -------------------------------------------------- tipos de habitación

  createRoomType: async ({ dispatch, commit }, { propertyId, ...payload }) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isCreating: true });
    try {
      await BookingSettingsAPI.createRoomType(propertyId, payload);
      await dispatch('show', propertyId);
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isCreating: false });
    }
  },

  updateRoomType: async (
    { dispatch, commit },
    { propertyId, id, ...payload }
  ) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isUpdating: true });
    try {
      await BookingSettingsAPI.updateRoomType(propertyId, id, payload);
      await dispatch('show', propertyId);
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isUpdating: false });
    }
  },

  deleteRoomType: async ({ dispatch, commit }, { propertyId, id }) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isDeleting: true });
    try {
      await BookingSettingsAPI.deleteRoomType(propertyId, id);
      await dispatch('show', propertyId);
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isDeleting: false });
    }
  },

  // ----------------------------------------------------- planes de tarifa

  createRatePlan: async (
    { dispatch, commit },
    { propertyId, roomTypeId, ...payload }
  ) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isCreating: true });
    try {
      await BookingSettingsAPI.createRatePlan(roomTypeId, payload);
      await dispatch('show', propertyId);
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isCreating: false });
    }
  },

  updateRatePlan: async (
    { dispatch, commit },
    { propertyId, roomTypeId, id, ...payload }
  ) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isUpdating: true });
    try {
      await BookingSettingsAPI.updateRatePlan(roomTypeId, id, payload);
      await dispatch('show', propertyId);
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isUpdating: false });
    }
  },

  // El servidor desactiva en lugar de borrar si ya se vendió con este plan. Se
  // devuelve la respuesta para que la pantalla pueda decir cuál de las dos pasó.
  deleteRatePlan: async (
    { dispatch, commit },
    { propertyId, roomTypeId, id }
  ) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isDeleting: true });
    try {
      const response = await BookingSettingsAPI.deleteRatePlan(roomTypeId, id);
      await dispatch('show', propertyId);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isDeleting: false });
    }
  },

  // ----------------------------------------------------- carga por rango

  applyInventory: async ({ commit }, { propertyId, ...payload }) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isApplying: true });
    try {
      const response = await BookingSettingsAPI.applyInventory(
        propertyId,
        payload
      );
      return response.data;
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isApplying: false });
    }
  },

  applyRates: async ({ commit }, { propertyId, ...payload }) => {
    commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isApplying: true });
    try {
      const response = await BookingSettingsAPI.applyRates(propertyId, payload);
      return response.data;
    } finally {
      commit(types.SET_BOOKING_SETTINGS_UI_FLAG, { isApplying: false });
    }
  },
};

export const mutations = {
  [types.SET_BOOKING_SETTINGS_UI_FLAG](_state, uiFlag) {
    _state.uiFlags = { ..._state.uiFlags, ...uiFlag };
  },
  [types.SET_BOOKING_PROPERTIES](_state, properties) {
    _state.properties = properties || [];
  },
  [types.SET_BOOKING_PROPERTY](_state, property) {
    const index = _state.properties.findIndex(item => item.id === property.id);
    if (index === -1) {
      _state.properties = [..._state.properties, property];
      return;
    }
    _state.properties = [
      ..._state.properties.slice(0, index),
      property,
      ..._state.properties.slice(index + 1),
    ];
  },
  [types.REMOVE_BOOKING_PROPERTY](_state, id) {
    _state.properties = _state.properties.filter(
      item => item.id !== Number(id)
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
